// TanStack Query hooks for the safeguards register and the evidence line page (T26). Lists are
// fresh for 30 s, the open line for 5 s, a past day never changes (plan §9). A write answers with
// the line, which replaces the cached live page; the register and the "after freeze" mark refresh.
import {
  queryOptions,
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import { today } from '@cis/shared';
import {
  createEvidenceApi,
  createHttp,
  type EvidenceLine,
  type EvidenceLineAction,
  type FrozenEvidence,
  type FrozenTask,
  type NewEvidenceRecord,
  type SafeguardFilters,
  type SafeguardSummary,
} from '@cis/api-client';

export const evidenceApi = createEvidenceApi(createHttp());

export const EVIDENCE_KEY = ['evidence'] as const;
const LIST_STALE_MS = 30_000;
const LINE_STALE_MS = 5_000;

export const safeguardsQuery = (filters: SafeguardFilters) =>
  queryOptions({
    queryKey: [...EVIDENCE_KEY, 'safeguards', filters] as const,
    queryFn: ({ signal }) => evidenceApi.safeguards(filters, signal),
    staleTime: LIST_STALE_MS,
  });

export function useSafeguards(filters: SafeguardFilters): UseQueryResult<SafeguardSummary[]> {
  return useQuery(safeguardsQuery(filters));
}

export const evidenceLineKey = (code: string, asOf: string | undefined) =>
  [...EVIDENCE_KEY, 'line', code, asOf ?? 'live'] as const;

// A past day never changes; today can still change, so it is fresh only as long as the live line
// (review F-R4).
const isClosedDay = (asOf: string | undefined): boolean => asOf !== undefined && asOf < today();

export const evidenceLineQuery = (code: string, asOf: string | undefined) =>
  queryOptions({
    queryKey: evidenceLineKey(code, asOf),
    queryFn: ({ signal }) => evidenceApi.line(code, { asOf, signal }),
    staleTime: isClosedDay(asOf) ? Infinity : LINE_STALE_MS,
  });

export function useEvidenceLine(
  code: string,
  asOf: string | undefined,
  enabled = true,
): UseQueryResult<EvidenceLine> {
  return useQuery({ ...evidenceLineQuery(code, asOf), enabled });
}

// Frozen views (T32): a frozen record never changes. Parsed with the evidence schemas, which allow
// for a freeze label made longer by the server's Leads redaction (review F-R1).
export const frozenLineKey = (freezeId: number, code: string) =>
  [...EVIDENCE_KEY, 'frozen', 'line', freezeId, code] as const;
export const frozenTaskKey = (freezeId: number, taskId: string) =>
  [...EVIDENCE_KEY, 'frozen', 'task', freezeId, taskId] as const;

export function useFrozenLine(freezeId: number, code: string): UseQueryResult<FrozenEvidence> {
  return useQuery({
    queryKey: frozenLineKey(freezeId, code),
    queryFn: ({ signal }) => evidenceApi.frozenLine(freezeId, code, signal),
    staleTime: Infinity,
  });
}

export function useFrozenTaskView(freezeId: number, taskId: string): UseQueryResult<FrozenTask> {
  return useQuery({
    queryKey: frozenTaskKey(freezeId, taskId),
    queryFn: ({ signal }) => evidenceApi.frozenTask(freezeId, taskId, signal),
    staleTime: Infinity,
  });
}

export type EvidenceWrite =
  | { readonly kind: 'record'; readonly record: NewEvidenceRecord }
  | {
      readonly kind: 'action';
      readonly action: EvidenceLineAction;
      readonly reason?: string;
      /** Set-gap only: the exception id (spec J5 step 2). */
      readonly exceptionId?: string;
    };

export interface EvidenceWriteInput {
  readonly write: EvidenceWrite;
  /** The line version the person saw (If-Match, constitution §4.7). */
  readonly version: number;
  /** The same key for a resend of the same input, so a retry adds one record (spec §8.8). */
  readonly idempotencyKey: string;
}

function send(code: string, input: EvidenceWriteInput): Promise<EvidenceLine> {
  const { write, version, idempotencyKey } = input;
  if (write.kind === 'record') {
    return evidenceApi.addRecord(code, write.record, { version, idempotencyKey });
  }
  return evidenceApi.act(code, {
    action: write.action,
    reason: write.reason,
    exceptionId: write.exceptionId,
    version,
    idempotencyKey,
  });
}

/** Adds a record, checks or returns the line. Every write disables the line's controls (§5.5). */
export function useEvidenceWrite(
  code: string,
): UseMutationResult<EvidenceLine, Error, EvidenceWriteInput> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: [...EVIDENCE_KEY, 'write', code],
    mutationFn: (input) => send(code, input),
    onSuccess: (line) => {
      queryClient.setQueryData(evidenceLineKey(code, undefined), line);
    },
    onSettled: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: [...EVIDENCE_KEY, 'safeguards'] }),
        queryClient.invalidateQueries({ queryKey: evidenceLineKey(code, today()) }),
        queryClient.invalidateQueries({ queryKey: ['freezes', 'mark', 'evidence_line', code] }),
      ]);
    },
  });
}
